import { useCallback, useEffect, useRef, useState } from "react";

import { fetchUsageMetrics } from "./api";
import type { TimeRange, UsageMetrics } from "./types";

const DEFAULT_POLL_INTERVAL_MS = 300_000; // 5 minutes

export interface UseUsageMetricsReturn {
  metrics: UsageMetrics | null;
  isLoading: boolean;
  error: string | null;
  timeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
  refresh: () => void;
}

export function useUsageMetrics(
  pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS
): UseUsageMetricsReturn {
  const [metrics, setMetrics] = useState<UsageMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>("24h");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadMetrics = useCallback(async (range: TimeRange) => {
    try {
      const data = await fetchUsageMetrics(range);
      setMetrics(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load usage metrics");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startPolling = useCallback(
    (range: TimeRange) => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        void loadMetrics(range);
      }, pollIntervalMs);
    },
    [loadMetrics, pollIntervalMs]
  );

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refresh = useCallback(() => {
    setIsLoading(true);
    void loadMetrics(timeRange);
  }, [loadMetrics, timeRange]);

  const handleSetTimeRange = useCallback((range: TimeRange) => {
    setIsLoading(true);
    setTimeRange(range);
  }, []);

  useEffect(() => {
    void loadMetrics(timeRange);
    startPolling(timeRange);

    function handleVisibilityChange(): void {
      if (document.hidden) {
        stopPolling();
      } else {
        void loadMetrics(timeRange);
        startPolling(timeRange);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadMetrics, timeRange, startPolling, stopPolling]);

  return {
    metrics,
    isLoading,
    error,
    timeRange,
    setTimeRange: handleSetTimeRange,
    refresh,
  };
}
